import { useState, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import Header from './components/layout/Header'
import Hero from './components/sections/Hero'
import About from './components/sections/About'
import Skills from './components/sections/Skills'
import Projects from './components/sections/Projects'
import Experience from './components/sections/Experience'
import Contact from './components/sections/Contact'
import Articles from './components/sections/Articles'
import CV from './components/sections/CV'
import Footer from './components/layout/Footer'
import './App.css'

const THEME_STORAGE_KEY = 'portfolio_theme'

const PageWrapper = ({ children, isHome }: { children: React.ReactNode; isHome: boolean }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 8 }}
    transition={{ duration: 0.2, ease: [0, 0, 0.2, 1] }}
    className={isHome ? '' : 'pt-24'}
  >
    {children}
  </motion.div>
)

function App() {
  const location = useLocation()
  const isHome = location.pathname === '/'

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false

    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (storedTheme) return storedTheme === 'dark'

    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode)
    window.localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light')
  }, [isDarkMode])

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-50 transition-colors duration-200">
      <Header
        isDarkMode={isDarkMode}
        onToggleDarkMode={() => setIsDarkMode((prev) => !prev)}
      />
      <main className="min-h-[calc(100vh-200px)]">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageWrapper isHome={isHome}><Hero /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper isHome={false}><About /></PageWrapper>} />
            <Route path="/skills" element={<PageWrapper isHome={false}><Skills /></PageWrapper>} />
            <Route path="/projects" element={<PageWrapper isHome={false}><Projects /></PageWrapper>} />
            <Route path="/experience" element={<PageWrapper isHome={false}><Experience /></PageWrapper>} />
            <Route path="/articles" element={<PageWrapper isHome={false}><Articles /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper isHome={false}><Contact /></PageWrapper>} />
            <Route path="/cv" element={<PageWrapper isHome={false}><CV /></PageWrapper>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  )
}

export default App
